"use client"

import { useActionState, useRef, useState } from "react"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import { signup } from "@lib/data/customer"
import GoogleAuthButton from "@modules/account/components/google-auth-button"
import { Mail, Lock, User, Phone } from "lucide-react"
import ReCAPTCHA from "react-google-recaptcha"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Register = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(signup, null)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError("Passwords do not match")
      return
    }
    setConfirmError(null)

    if (!recaptchaToken) {
      e.preventDefault()
      setRecaptchaError("Please verify you are not a robot")
      return
    }
    setRecaptchaError(null)
  }

  return (
    <div data-testid="register-page">
      <h2 className="text-2xl font-black text-center text-gray-900 mb-1 uppercase tracking-wide">
        Create Account
      </h2>
      <p className="text-center text-sm text-gray-500 mb-8">
        Join the UK Soccer Shop squad
      </p>

      <form className="w-full" action={formAction} onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* First Name + Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                First Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  name="first_name"
                  type="text"
                  placeholder="First Name"
                  required
                  autoComplete="given-name"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                  data-testid="first-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                Last Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  name="last_name"
                  type="text"
                  placeholder="Last Name"
                  required
                  autoComplete="family-name"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                  data-testid="last-name-input"
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              E-Mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="email"
                type="email"
                placeholder="E-Mail"
                required
                autoComplete="email"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Phone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="phone"
                type="tel"
                placeholder="Phone (optional)"
                autoComplete="tel"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="password-input"
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="confirm_password"
                type="password"
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
        </div>

        <ErrorMessage
          error={confirmError ?? formState?.error}
          data-testid="register-error"
          className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
        />

        <label className="flex items-center gap-2 text-sm text-gray-600 mt-4 mb-6 cursor-pointer">
          <input
            name="accepts_marketing"
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300"
            data-testid="accepts-marketing-checkbox"
          />
          Keep me up to date on news and exclusive offers
        </label>

        <div className="mb-6">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
            onChange={(token) => {
              setRecaptchaToken(token)
              setRecaptchaError(null)
            }}
            onExpired={() => setRecaptchaToken(null)}
          />
          <ErrorMessage
            error={recaptchaError}
            data-testid="recaptcha-error"
            className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          />
        </div>

        <SubmitButton
          data-testid="register-button"
          className="w-full py-3.5 bg-[#1a1a2e] text-white font-bold rounded-lg hover:bg-[#2a2a3e] transition-colors flex items-center justify-center gap-2"
        >
          Create Account
          <span aria-hidden="true">&rarr;</span>
        </SubmitButton>
      </form>

      {/* OR divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <p className="text-center text-sm text-gray-500 mb-4">Sign Up With...</p>

      <GoogleAuthButton />
    </div>
  )
}

export default Register
